import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowLeft, Calendar, Mail } from 'lucide-react';
import { tradesPage, contactInfo } from '../mock';
import VideoModal from '../components/VideoModal';
import LogoTicker from '../components/LogoTicker';
import Seo from '../components/Seo';
import { breadcrumb } from '../lib/seoSchemas';

const thumbOf = (id) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;

const VideoCard = ({ v, onPlay }) => (
  <button
    onClick={() => onPlay(v.youtubeId, v.title)}
    data-testid={`trades-video-${v.id}`}
    className="group relative rounded-2xl overflow-hidden text-left border border-white/10 hover:border-[#c9a85f]/60 transition-all aspect-video"
  >
    <img src={thumbOf(v.youtubeId)} alt={v.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#c9a85f]/90 text-black group-hover:scale-110 transition-transform shadow-xl">
        <Play size={22} className="fill-black ml-1" />
      </span>
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-5">
      <h3 className="font-display font-semibold text-white text-base md:text-lg leading-tight uppercase group-hover:text-[#c9a85f] transition-colors">{v.title}</h3>
    </div>
  </button>
);

export default function Trades() {
  const [video, setVideo] = useState(null);
  const play = useCallback((youtubeId, title) => setVideo({ youtubeId, title }), []);
  const close = useCallback(() => setVideo(null), []);
  const { featured, videos } = tradesPage;

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative">
      <Seo
        title="Video for Trades & Construction | Simply Gold Productions"
        description="Video production for trades and construction companies in BC. Show real crews and job sites, build trust and win bigger contracts with Simply Gold Productions."
        path="/trades"
        type="video.other"
        jsonLd={breadcrumb('Trades & Construction', '/trades')}
      />
      <header className="sticky top-0 z-40 bg-[#0a0a0a]/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="trades-logo-home">
            <img src="/sgp-logo-header.png" alt="Simply Gold Productions logo" width="1222" height="629" className="h-12 md:h-14 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="trades-back-link" className="hidden sm:inline-flex items-center gap-2 text-white/70 hover:text-[#c9a85f] text-sm font-medium transition-colors">
              <ArrowLeft size={16} /> Back to site
            </Link>
            <a href={`mailto:${contactInfo.email}`} data-testid="trades-contact-btn" className="inline-flex items-center gap-2 bg-[#c9a85f] hover:bg-[#d8b970] text-black font-display font-semibold text-sm px-5 py-2.5 rounded-md transition-colors tracking-wide">
              <Mail size={15} /> Contact
            </a>
          </div>
        </div>
      </header>

      <main>
      {/* Featured trades case study */}
      <section className="max-w-7xl mx-auto px-6 pt-8 md:pt-10" data-testid="trades-banner">
        <button
          onClick={() => play(featured.youtubeId, featured.title)}
          data-testid="trades-banner-button"
          className="group relative block w-full rounded-3xl overflow-hidden aspect-video md:aspect-[21/9] border border-white/10"
        >
          <img src={featured.thumb} alt={featured.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20 group-hover:via-black/35 transition-colors" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="font-display tracking-[0.32em] text-[#c9a85f] text-xs md:text-sm uppercase mb-4">Synscape · Okanagan, BC</p>
            <h1 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-6xl uppercase leading-[0.98] max-w-5xl">
              Building One of the Okanagan's Biggest Backyard Golf Greens
            </h1>
            <p className="mt-5 text-white/70 text-sm md:text-base max-w-xl">
              A behind-the-build story about the people, precision and craftsmanship behind a 2,700-square-foot private green.
            </p>
            <span className="mt-8 flex items-center justify-center w-16 h-16 rounded-full bg-[#c9a85f]/90 text-black group-hover:scale-110 transition-transform shadow-xl">
              <Play size={26} className="fill-black ml-1" />
            </span>
          </div>
        </button>
      </section>

      {/* Video gallery — 3 per row, manually ordered */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20" data-testid="trades-gallery">
        <div className="mb-12 text-center">
          <h2 className="font-display font-bold text-white text-3xl md:text-4xl uppercase">Trades Work In Action</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((v) => (
            <VideoCard key={v.id} v={v} onPlay={play} />
          ))}
        </div>
      </section>

      <LogoTicker category="Trades" heading="Trusted By Trades & Construction Teams" />

      <section className="border-t border-white/10 bg-[#0c0c0c]">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display font-bold text-white text-3xl md:text-4xl uppercase">Ready to show what you build?</h2>
          <p className="text-white/55 mt-4 max-w-xl mx-auto">Let's put your crews, your process and your quality on camera — and turn it into more closed jobs.</p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={contactInfo.calendly} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-[#c9a85f] hover:bg-[#d8b970] text-black font-display font-semibold px-7 py-3.5 rounded-md transition-colors tracking-wide">
              <Calendar size={18} /> Book A Meeting
            </a>
            <a href={`mailto:${contactInfo.email}`} className="inline-flex items-center gap-2 text-white/85 hover:text-[#c9a85f] font-display font-medium px-4 py-3.5 tracking-wide transition-colors">
              <Mail size={18} /> {contactInfo.email}
            </a>
          </div>
        </div>
      </section>
      </main>


      <footer className="py-8 text-center text-white/40 text-xs border-t border-white/10">
        © {new Date().getFullYear()} {contactInfo.company} — {contactInfo.tagline}
      </footer>

      {video && <VideoModal youtubeId={video.youtubeId} title={video.title} onClose={close} />}
    </div>
  );
}
